import ReactDOM from "react-dom";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { APIClient } from "api/apiCore";
import * as url from "api/urls"
import { backdropStyle, modalStyle } from "DialogProviderShare";
import { Utils } from "helpers/utils";
import { tb_HookData, tb_Hook, HookEvents, HookBodyTypes, HookMethods, HookAuths, HookDataSection } from "types";
import AppSimpleBar from "components/AppSimpleBar";
import { FormControlLabel, Switch } from "@mui/material";
import Loader from "components/Loader";
import LoadingIconWithText from "components/LoadingIconWithText";
import ReactJson from "react-json-view";
import { createSelector } from "reselect";
import { useRedux } from "hooks";
import { SettingsState } from "redux/settings/types";

// 1. Create context at module level
const ColorContext = React.createContext<ReturnType<typeof useColors>>(null!);
const useC = () => React.useContext(ColorContext);

// 2. baseInput becomes a function that takes C
const makeBaseInput = (C: ReturnType<typeof useColors>): React.CSSProperties => ({
  background: C.bg,
  border: `1px solid ${C.border}`,
  borderRadius: 7,
  color: C.text,
  padding: "9px 12px",
  outline: "none",
  width: "100%",
  fontFamily: font,
  boxSizing: "border-box",
  transition: "border-color 0.15s",
});

// ── Design tokens ──────────────────────────────────────────────────────────
function useColors(isDark: boolean) {
  return isDark ? {
    bg: "#09090b",
    surface: "#111113",
    border: "#27272a",
    borderFocus: "#a78bfa",
    text: "#fafafa",
    muted: "#71717a",
    subtle: "#3f3f46",
    accent: "#a78bfa",
    accentDim: "#a78bfa22",
    green: "#34d399",
    greenDim: "#34d39922",
    red: "#f87171",
    redDim: "#f8717122",
    amber: "#fbbf24",
    amberDim: "#fbbf2422",
  } : {
    bg: "#ffffff",
    surface: "#f8fafc",
    border: "#e2e8f0",
    borderFocus: "#7c3aed",
    text: "#0f172a",
    muted: "#94a3b8",
    subtle: "#cbd5e1",
    accent: "#7c3aed",
    accentDim: "#7c3aed18",
    green: "#059669",
    greenDim: "#05966918",
    red: "#dc2626",
    redDim: "#dc262618",
    amber: "#d97706",
    amberDim: "#d9770618",
  };
}

const font = "inherit";

// ── Tabs ───────────────────────────────────────────────────────────────────
const TABS = [
  { id: "general", label: "General", icon: "⚙" },
  { id: "header", label: "Header", icon: "◈" },
  { id: "body", label: "Body", icon: "{}" },
  { id: "test", label: "Test", icon: "#" }
] as const;
type TabId = typeof TABS[number]["id"];

// ── Styled Input ───────────────────────────────────────────────────────────
function StyledInput(props: React.InputHTMLAttributes<HTMLInputElement> & { icon?: string }) {
  const C = useC();                          // 👈 from context
  const baseInput = makeBaseInput(C);        // 👈 derived here  
  const [f, setF] = useState(false);
  const { icon, style, ...rest } = props;
  return (
    <div style={{ position: "relative" }}>
      {icon && (
        <span style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", /*fontSize: 13,*/ color: C.muted, pointerEvents: "none" }}>
          {icon}
        </span>
      )}
      <input
        {...rest}
        style={{ ...baseInput, borderColor: f ? C.borderFocus : C.border, paddingLeft: icon ? 32 : 12, ...style }}
        onFocus={e => { setF(true); props.onFocus?.(e); }}
        onBlur={e => { setF(false); props.onBlur?.(e); }}
      />
    </div>
  );
}

// ── Styled Select ──────────────────────────────────────────────────────────
function StyledSelect(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  const C = useC();                          // 👈 from context
  const baseInput = makeBaseInput(C);        // 👈 derived here  
  const [f, setF] = useState(false);
  return (
    <select
      {...props}
      style={{
        ...baseInput, borderColor: f ? C.borderFocus : C.border, cursor: "pointer",
        appearance: "none",
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%2371717a'/%3E%3C/svg%3E")`,
        backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center", paddingRight: 32,
        ...props.style,
      }}
      onFocus={e => { setF(true); props.onFocus?.(e); }}
      onBlur={e => { setF(false); props.onBlur?.(e); }}
    />
  );
}

function EnumSelect<T extends Record<string, string>>({
  enumObj, value, onChange, placeholder, disabled
}: { enumObj: T; value: string; onChange: (v: string) => void; placeholder?: string, disabled: boolean | undefined }) {
  return (
    <StyledSelect value={value} onChange={e => onChange(e.target.value)} disabled={disabled}>
      {placeholder && <option value="">{placeholder}</option>}
      {Object.values(enumObj).map(v => (
        <option key={v} value={v}>{Utils.SplitByCase(v)}</option>
      ))}
    </StyledSelect>
  );
}

function Field({ label, hint, children, action }: { label: string; hint?: string; children: React.ReactNode; action?: React.ReactNode }) {
  const C = useC()
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <label style={{ fontSize: 13, fontWeight: 600, letterSpacing: "0.07em", color: C.muted }}>
          {label}
        </label>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {action && action}
          {hint && <span style={{ fontSize: 11, color: C.subtle }}>{hint}</span>}
        </div>
      </div>
      {children}
    </div>
  );
}

function TabBar({ active, onChange, counts, allowedit }: {
  active: TabId; onChange: (t: TabId) => void; counts: Record<string, number>; allowedit: boolean;
}) {
  const C = useC()
  return (
    <div style={{ display: "flex", borderBottom: `1px solid ${C.border}`, marginBottom: 28, gap: 2 }}>
      {TABS.filter(x => allowedit || x.id !== "test").map(tab => {
        const isActive = tab.id === active;
        const count = counts[tab.id] ?? 0;
        return (
          <button
            key={tab.id}
            onClick={() => onChange(tab.id)}
            style={{
              background: "none", border: "none", cursor: "pointer",
              fontFamily: font, /*fontSize: 13,*/ fontWeight: isActive ? 700 : 400,
              color: isActive ? C.accent : C.muted,
              padding: "10px 18px",
              borderBottom: isActive ? `2px solid ${C.accent}` : "2px solid transparent",
              marginBottom: -1,
              display: "flex", alignItems: "center", gap: 7,
              transition: "color 0.15s",
            }}
          >
            <span style={{ fontSize: 14 }}>{tab.icon}</span>
            {tab.label}
            {count > 0 && (
              <span style={{
                background: isActive ? C.accentDim : C.border,
                color: isActive ? C.accent : C.muted,
                borderRadius: 20, /*fontSize: 10,*/ fontWeight: 700,
                padding: "1px 7px", lineHeight: "18px",
              }}>
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ── KV Row ─────────────────────────────────────────────────────────────────
function KVRow({ row, onChange, onRemove, disabled }: {
  row: tb_HookData; onChange: (r: tb_HookData) => void; onRemove: () => void; disabled: boolean;
}) {
  const C = useC()
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 36px", gap: 8, alignItems: "center" }}>
      <StyledInput placeholder="Key" disabled={disabled} value={row?.name ?? ""} onChange={e => onChange({ ...row, name: e.target.value })} />
      <StyledInput placeholder="Value" disabled={disabled} value={row?.value ?? ""} onChange={e => onChange({ ...row, value: e.target.value })} />
      {!disabled && (
        <button
          onClick={onRemove}
          style={{
            background: C.redDim, border: `1px solid ${C.red}33`, borderRadius: 7,
            color: C.red, cursor: "pointer", /*fontSize: 14,*/
            width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >✕</button>
      )}
    </div>
  );
}

function KVTable({ rows, onUpdate, onAdd, onRemove, addLabel, disabled }: {
  rows: tb_HookData[]; onUpdate: (i: number, r: tb_HookData) => void;
  onAdd: () => void; onRemove: (i: number) => void; addLabel: string;
  disabled: boolean
}) {
  const C = useC()
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {rows.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 36px", gap: 8, padding: "0 2px" }}>
          {["Key", "Value", ""].map((h, i) => (
            <span key={i} style={{ /*fontSize: 10, */fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: C.subtle }}>{h}</span>
          ))}
        </div>
      )}
      {rows.length === 0 && (
        <div style={{ textAlign: "center", padding: "32px 0", color: C.subtle, /*fontSize: 12,*/ border: `1px dashed ${C.border}`, borderRadius: 10 }}>
          No entries yet
        </div>
      )}
      {rows.map((row, i) => (
        <KVRow key={row.seq} row={row} onChange={r => onUpdate(i, r)} onRemove={() => onRemove(i)} disabled={disabled} />
      ))}
      {!disabled && (
        <button
          onClick={onAdd}
          style={{
            background: "none", border: `1px dashed ${C.subtle}`, borderRadius: 8,
            color: C.muted, cursor: "pointer", fontFamily: font, /*fontSize: 12,*/ fontWeight: 600,
            padding: "9px 0", width: "100%", letterSpacing: "0.05em",
          }}
        >
          + {addLabel}
        </button>
      )}
    </div>
  );
}

// ── Body Type Picker ───────────────────────────────────────────────────────
function BodyTypePicker({ value, onChange }: { value: HookBodyTypes; onChange: (v: HookBodyTypes) => void }) {
  const C = useC()
  const styles: Record<HookBodyTypes, { bg: string; border: string; color: string }> = {
    [HookBodyTypes.None]: { bg: C.subtle + "33", border: C.subtle, color: C.muted },
    [HookBodyTypes.FormData]: { bg: C.amberDim, border: C.amber, color: C.amber },
    [HookBodyTypes.Json]: { bg: C.greenDim, border: C.green, color: C.green },
  };

  return (
    <div style={{ display: "flex", gap: 8 }}>
      {(Object.values(HookBodyTypes) as HookBodyTypes[]).map(bt => {
        const active = value === bt;
        const s = styles[bt];
        return (
          <button
            key={bt}
            onClick={() => onChange(bt)}
            style={{
              background: active ? s.bg : "none",
              border: `1px solid ${active ? s.border : C.border}`,
              borderRadius: 8, color: active ? s.color : C.muted,
              cursor: "pointer", fontFamily: font, /*fontSize: 12,*/ fontWeight: 700,
              padding: "7px 18px", transition: "all 0.15s", letterSpacing: "0.05em",
            }}
          >
            {Utils.SplitByCase(bt)}
          </button>
        );
      })}
    </div>
  );
}

export function DialogProviderHookInfoPortal({ hookId, onClose }: { hookId: number | null, onClose: (v: any) => void }) {
  const { dispatch, useAppSelector } = useRedux();
  const storeData = createSelector(
    (state: any) => state.Settings,
    (state: any) => state.Profile2,
    (st: SettingsState, p2) => ({
      userConfig: st.userConfig,
      isAdmin: p2.isAdmin,
      curUserId: p2.userId,
    })
  );
  const { isAdmin, userConfig, curUserId } = useAppSelector(storeData);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  let _id = 0;
  const uid = () => ++_id;
  const [tab, setTab] = useState<TabId>("general");
  const [hook, setHook] = useState<tb_Hook>({
    method: HookMethods.POST,
    hookUrl: "",
    authMethod: HookAuths.None,
    bearerToken: "",
    purpose: "",
    event: null,
    bodyType: HookBodyTypes.None,
  });

  const [headers, setHeaders] = useState<tb_HookData[]>([]);
  const [bodyRows, setBodyRows] = useState<tb_HookData[]>([]);
  const [saved, setSaved] = useState(false);
  const [showToken, setShowToken] = useState(false);

  const addHeader = () =>
    setHeaders(r => [...r, { id: uid(), dataSection: HookDataSection.Header, name: "", value: "", seq: r.length + 1 }]);
  const updateHeader = (i: number, row: tb_HookData) => setHeaders(r => r.map((x, idx) => idx === i ? row : x));
  const removeHeader = (i: number) => setHeaders(r => r.filter((_, idx) => idx !== i));

  const addBodyRow = () => {
    const section = hook.bodyType === HookBodyTypes.FormData ? HookDataSection.FormData : HookDataSection.Json;
    setBodyRows(r => [...r, { id: uid(), dataSection: section, name: "", value: "", seq: r.length + 1 }]);
  };
  const updateBodyRow = (i: number, row: tb_HookData) => setBodyRows(r => r.map((x, idx) => idx === i ? row : x));
  const removeBodyRow = (i: number) => setBodyRows(r => r.filter((_, idx) => idx !== i));

  const hasBody = hook.bodyType === HookBodyTypes.FormData || hook.bodyType === HookBodyTypes.Json;
  const tabCounts = { general: 0, header: headers.filter(r => r.name || r.value).length, body: bodyRows.filter(r => r.name || r.value).length };
  const isBearer = hook.authMethod === HookAuths.BearerToken;
  const C = useColors(Utils.IsDarkTheme());
  const [allowEdit, setAllowEdit] = useState<boolean>(false);

  useEffect(() => {
    try {
      setAllowEdit(false);
      if (hookId && hookId > 0) {
        setAllowEdit(false);
        setIsLoading(true);
        new APIClient().fnPostForm(url.HOOK_LOAD, { hookId: hookId })
          .then(rs => {
            // console.log('HOOK_LOAD:', rs);
            if (rs?.hookId) {
              const headers = rs?.hookData?.filter((x: any) => x.dataSection == HookDataSection.Header)?.sort((a: any, b: any) => a?.seq - b?.seq);
              const body = rs?.hookData?.filter((x: any) => x.dataSection == rs.bodyType)?.sort((a: any, b: any) => a?.seq - b?.seq);
              setHook(rs);
              setHeaders(headers);
              setBodyRows(body);
              setAllowEdit(isAdmin || rs.createBy == curUserId || rs.updateBy == curUserId);
            } else {
              alert('Not found hook');
              onClose(null);
            }

            setIsLoading(false);
          })
          .catch(e2 => {
            Utils.HandleException(e2, true);
            setIsLoading(false);
          });
      } else {
        setAllowEdit(true);
        setHook({ enabled: true, method: HookMethods.POST });
        setHeaders([{ seq: 1, name: "Content-Type", value: "application/json" }]);
        setBodyRows([]);
      }
    } catch (e) {
      Utils.HandleException(e, true);
    }
  }, [hookId]);

  let errMsg = "";
  const err = (msg?: string): null => {
    alert(msg ?? errMsg);
    return null;
  }


  const getDataForSave = (validate: boolean) => {
    const h1 = headers.map((x, idx) => ({ ...x, dataSection: HookDataSection.Header, seq: idx + 1 }));
    const b1 = bodyRows.map((x, idx) => ({ ...x, dataSection: hook.bodyType, seq: idx + 1 }));
    const data = {
      ...hook,
      hookData: [...h1, ...b1],
      // header: [...headers],
      // body: [...bodyRows]
    };

    if (validate) {
      if (!data.purpose) return err("กรุณาระบุ Description");
      if (!data.event) return err("กรุณาระบุ Event");
      if (!data.method) return err("กรุณาระบุ Method");
      if (!data.hookUrl) return err("กรุณาระบุ callback url");
      if (data.authMethod == HookAuths.BearerToken && !data.bearerToken) return err("กรุณาระบุ Baerer Token");
    }

    // finaly
    return data;
  }

  const handleSave = () => {
    const data2save = getDataForSave(true);
    // console.log('data2save:', data2save);
    if (!data2save) return;

    setIsLoading(true);
    new APIClient().fnPostBody(url.HOOK_SAVE, data2save)
      .then(rs => {
        const serviceResponse = rs;
        // console.log('hook save:', serviceResponse);
        if (serviceResponse.success) {
          onClose(serviceResponse.data);
        } else {
          alert(serviceResponse.message);
        }

        setIsLoading(false);
      })
      .catch(ex => {
        Utils.HandleException(ex, true);
        setIsLoading(false);
      })

  };

  const [testResult, setTestResult] = useState<any>();
  const [testingHook, setTestingHook] = useState<boolean>(false);
  const fnTestHook = async () => {
    try {
      setTab("test");
      setTestingHook(true);
      const data = getDataForSave(false);
      new APIClient().fnPostBody(url.HOOK_PERFORM, data)
        .then(rs => {
          setTestResult(rs);
          // console.log('HOOK_PERFORM:', rs);
          setTestingHook(false);
        })
        .catch(e2 => {
          Utils.HandleException(e2, true);
          setTestResult(e2);
          setTestingHook(false);
        });

    } catch (e) {
      Utils.HandleException(e, true);
      setTestResult(e);
      setTestingHook(false);
    }
  }

  const fnDel = async () => {
    const rs = await Utils.SweetConfirm("ยืนยันการลบ");
    if (rs.isConfirmed) {
      setIsLoading(true);
      new APIClient().fnPostForm(url.HOOK_DELETE, { hookId: hookId })
        .then(rs => {
          setIsLoading(false);
          onClose(null);
        })
        .catch(er => {
          setIsLoading(false);
          Utils.HandleException(er, true);
        });
    }
  }

  const handleEsc = useCallback((event: any) => {
    if (event.key === "Escape") {
      onClose(-1);
    }
  }, [onClose]);

  useEffect(() => {
    // Add listener when the modal mounts
    window.addEventListener("keydown", handleEsc);

    // Clean up listener when the modal unmounts
    return () => {
      window.removeEventListener("keydown", handleEsc);
    };
  }, [handleEsc]);

  const [copiedField, setCopiedField] = useState<string | null>(null);
  const handleCopy = (field: string) => {
    navigator.clipboard.writeText(field);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 1500);
  };

  return ReactDOM.createPortal(
    <ColorContext.Provider value={C}>
      <div style={backdropStyle} >
        {isLoading && (<Loader />)}
        <div style={modalStyle} >

          <div style={{ height: 700, background: C.bg, fontFamily: font, color: C.text, padding: "10px 24px" }} className="">
            <style>{`
        /*@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;600;700&display=swap');*/
        * { box-sizing: border-box; }
        ::placeholder { color: #3f3f46 !important; }
        select option { background: #18181b; color: #fafafa; }
        @keyframes slideDown { from { opacity:0; transform:translateY(-8px) } to { opacity:1; transform:translateY(0) } }
      `}</style>

            <div style={{ maxWidth: 680, margin: "0 auto" }}>

              {/* Page header */}
              <div style={{ marginBottom: 10, display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                <div>
                  <h2 style={{ /*fontSize: 22,*/ fontWeight: 700, margin: 0, letterSpacing: "-0.02em" }}>Hook Manager </h2>
                  {/* {allowEdit ? "[edit]" : "[read]"} */}
                </div>

                {/* Live status chips */}
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end", paddingTop: 4 }}>
                  {isBearer && (
                    <span style={{ background: C.accentDim, border: `1px solid ${C.accent}44`, color: C.accent, borderRadius: 6, fontSize: 12, fontWeight: 700, padding: "3px 9px", letterSpacing: "0.06em" }}>
                      🔐 Bearer
                    </span>
                  )}
                  {hook.event && (
                    <span style={{ background: C.greenDim, border: `1px solid ${C.green}44`, color: C.green, borderRadius: 6, fontSize: 12, fontWeight: 700, padding: "3px 9px", letterSpacing: "0.06em" }}>
                      {Utils.SplitByCase(hook.event)}
                    </span>
                  )}
                  {hook.bodyType && hook.bodyType !== HookBodyTypes.None && (
                    <span style={{ background: C.amberDim, border: `1px solid ${C.amber}44`, color: C.amber, borderRadius: 6, fontSize: 12, fontWeight: 700, padding: "3px 9px", letterSpacing: "0.06em" }}>
                      {hook.bodyType}
                    </span>
                  )}
                </div>
              </div>

              {/* Card */}
              <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: "10px 28px" }}>
                <TabBar active={tab} onChange={setTab} counts={tabCounts} allowedit={allowEdit} />

                <AppSimpleBar style={{ height: 470 }}>
                  {/* ── General ── */}
                  {tab === "general" && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 18, animation: "slideDown 0.18s ease" }}>
                      <Field label="Description" action={
                        <div className="d-flex justify-content-between align-items-center w-100">
                          <FormControlLabel
                            control={<Switch checked={hook.enabled ?? false} readOnly={!allowEdit} onChange={(e) => setHook(h => ({ ...h, enabled: allowEdit ? e.target.checked : h.enabled }))} />}
                            label={"Enabled"} />
                        </div>
                      }
                      >
                        <StyledInput
                          icon="✦"
                          placeholder="Describe what this webhook does…"
                          value={hook.purpose ?? ""}
                          readOnly={!allowEdit}
                          onChange={e => setHook(h => ({ ...h, purpose: e.target.value }))}
                        />
                      </Field>

                      <Field label="Trigger Event">
                        <EnumSelect
                          enumObj={HookEvents}
                          value={hook.event ?? ""}
                          disabled={!allowEdit}
                          onChange={v => setHook(h => ({ ...h, event: v as HookEvents }))}
                          placeholder="Select event…"
                        />
                      </Field>

                      <div style={{ display: "grid", gridTemplateColumns: "100px 1fr", gap: 12 }}>
                        <Field label="Method">
                          <EnumSelect
                            enumObj={HookMethods}
                            value={hook.method ?? ""}
                            disabled={!allowEdit}
                            onChange={v => setHook(h => ({ ...h, method: v as HookMethods }))}
                          />
                        </Field>
                        <Field label="Callback URL">
                          <StyledInput
                            icon="⇒"
                            placeholder="https://your-endpoint.com/webhook"
                            readOnly={!allowEdit}
                            value={hook.hookUrl ?? ""}
                            onChange={e => setHook(h => ({ ...h, hookUrl: e.target.value }))}
                          />
                        </Field>
                      </div>

                      <Field label="Authorization">
                        <EnumSelect
                          enumObj={HookAuths}
                          value={hook.authMethod ?? ""}
                          disabled={!allowEdit}
                          onChange={v => {
                            setHook(h => ({ ...h, authMethod: v as HookAuths, bearerToken: "" }));
                            setShowToken(false);
                          }}
                        />
                      </Field>

                      {/* Bearer Token panel */}
                      {isBearer && (
                        <div style={{
                          background: C.accentDim, border: `1px solid ${C.accent}33`,
                          borderRadius: 10, padding: "16px 18px",
                          animation: "slideDown 0.2s ease",
                        }}>
                          <Field label="Bearer Token" hint="stored securely">
                            <div style={{ position: "relative" }}>
                              <StyledInput
                                icon="🔑"
                                type={showToken ? "text" : "password"}
                                placeholder="eyJhbGciOiJIUzI1NiIs…"
                                value={hook.bearerToken ?? ""}
                                onChange={e => setHook(h => ({ ...h, bearerToken: e.target.value }))}
                                style={{ paddingRight: 44 }}
                                readOnly={!allowEdit}
                              />
                              <button
                                onClick={() => setShowToken(s => !s)}
                                style={{
                                  position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
                                  background: "none", border: "none", cursor: "pointer", color: C.muted, /*fontSize: 14,*/ padding: 4,
                                }}
                                title={showToken ? "Hide" : "Show"}
                              >
                                {showToken ? "🙈" : "👁"}
                              </button>
                            </div>
                          </Field>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── Header ── */}
                  {tab === "header" && (
                    <div style={{ animation: "slideDown 0.18s ease" }}>
                      <p style={{ fontSize: 12, color: C.muted, marginTop: 0, marginBottom: 20 }}>
                        HTTP headers sent with every request to the callback URL.<br />
                        <span className="text-primary">Content-Type</span> ca be &nbsp;
                        <span className="text-warning">application/x-www-form-urlencoded</span>&nbsp;or&nbsp;
                        <span className="text-warning">application/json</span>
                      </p>

                      <KVTable
                        rows={headers}
                        onUpdate={updateHeader}
                        onAdd={addHeader}
                        onRemove={removeHeader}
                        disabled={!allowEdit}
                        addLabel="Add Header"
                      />
                    </div>
                  )}

                  {/* ── Body ── */}
                  {tab === "body" && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 22, animation: "slideDown 0.18s ease" }}>
                      <Field label="Body Type">
                        <BodyTypePicker
                          value={hook.bodyType ?? HookBodyTypes.None}
                          onChange={v => {
                            const newSection = v === HookBodyTypes.FormData ? HookDataSection.FormData : HookDataSection.Json;
                            setHook(h => ({ ...h, bodyType: v as HookBodyTypes }));
                            setBodyRows(r => r.map(row => ({ ...row, dataSection: newSection })));
                          }}
                        />
                      </Field>

                      {!hasBody ? (
                        <div style={{
                          textAlign: "center", padding: "36px 0", color: C.subtle,
                          fontSize: 12, border: `1px dashed ${C.border}`, borderRadius: 10,
                        }}>
                          Select <strong style={{ color: C.amber }}>Form Data</strong> or{" "}
                          <strong style={{ color: C.green }}>JSON</strong> to define body fields
                        </div>
                      ) : (
                        <div style={{ animation: "slideDown 0.18s ease" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 14, marginTop: 0 }}>
                            {/* <span style={{
                              background: hook.bodyType === HookBodyTypes.Json ? C.greenDim : C.amberDim,
                              color: hook.bodyType === HookBodyTypes.Json ? C.green : C.amber,
                              border: `1px solid ${hook.bodyType === HookBodyTypes.Json ? C.green : C.amber}44`,
                              borderRadius: 6, fontSize: 11, fontWeight: 700, padding: "3px 10px", letterSpacing: "0.05em",
                            }}>
                              {hook.bodyType === HookBodyTypes.Json ? "{ } JSON Fields" : "⊟ Form Data Fields"}
                            </span>
                            <span style={{ fontSize: 11, color: C.subtle }}>
                              {bodyRows.length} field{bodyRows.length !== 1 ? "s" : ""}
                            </span> */}
                            {Utils.GetAvaliableHookField(hook.event ?? null).map(field => (
                              <span key={field} className="hand" onClick={(e) => handleCopy('{' + field + '}')} style={{
                                background: hook.bodyType === HookBodyTypes.Json ? C.greenDim : C.amberDim,
                                color: hook.bodyType === HookBodyTypes.Json ? C.green : C.amber,
                                border: `1px solid ${hook.bodyType === HookBodyTypes.Json ? C.green : C.amber}44`,
                                borderRadius: 6, fontSize: 11, fontWeight: 700, padding: "2px 2px", letterSpacing: "0.05em",
                              }}
                                title={copiedField === field ? "Copied!" : "Click to copy"}
                              >
                                {'{' + field + '}'}
                                {/* <i className="ms-1 fa fa-copy"></i> */}
                              </span>
                            ))}

                          </div>
                          <KVTable
                            rows={bodyRows}
                            onUpdate={updateBodyRow}
                            onAdd={addBodyRow}
                            onRemove={removeBodyRow}
                            disabled={!allowEdit}
                            addLabel={`Add ${hook.bodyType === HookBodyTypes.Json ? "JSON" : "Form"} Field`}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {tab === "test" && (
                    <div style={{ animation: "slideDown 0.18s ease" }}>

                      <div>
                        {testingHook && (<Loader />)}
                        {!testingHook && (
                          <ReactJson
                            src={testResult}
                            theme="monokai"
                            collapsed={false}
                            displayDataTypes={false}
                          />
                        )}
                      </div>
                    </div>
                  )}

                </AppSimpleBar>
              </div>

              {/* Footer actions */}
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 18, gap: 10 }}>
                <div style={{ display: "flex", gap: 10 }}>
                  {allowEdit && (
                    <button
                      onClick={fnDel}
                      style={{
                        background: C.redDim, border: `1px solid ${C.red}33`, borderRadius: 9,
                        color: C.red, cursor: "pointer", fontFamily: font, /*fontSize: 13,*/ fontWeight: 600,
                        padding: "10px 22px",
                      }}
                    >
                      Delete
                    </button>
                  )}
                  {allowEdit && (
                    <button
                      onClick={() => fnTestHook()}
                      style={{
                        background: C.amberDim, border: `1px solid ${C.amber}33`, borderRadius: 9,
                        color: C.amber, cursor: "pointer", fontFamily: font, fontWeight: 600,
                        padding: "10px 22px"
                      }}
                    >
                      {testingHook && (<LoadingIconWithText loadingText={""} />)}
                      Test
                    </button>
                  )}
                </div>
                <div style={{ display: "flex", gap: 10 }}>
                  <button
                    onClick={() => {
                      setHook({ method: HookMethods.POST, hookUrl: "", authMethod: HookAuths.None, bearerToken: "", purpose: "", event: null, bodyType: HookBodyTypes.None });
                      setHeaders([]); setBodyRows([]);
                      onClose(-1);
                    }}
                    style={{
                      background: "none", border: `1px solid ${C.border}`, borderRadius: 9,
                      color: C.muted, cursor: "pointer", fontFamily: font, /*fontSize: 13,*/ fontWeight: 600,
                      padding: "10px 22px",
                    }}
                  >
                    Close
                  </button>
                  {allowEdit && (
                    <button
                      onClick={handleSave}
                      style={{
                        background: saved ? C.greenDim : C.accentDim,
                        border: `1px solid ${saved ? C.green : C.accent}`,
                        borderRadius: 9, color: saved ? C.green : C.accent,
                        cursor: "pointer", fontFamily: font, /*fontSize: 13,*/ fontWeight: 700,
                        padding: "10px 32px", transition: "all 0.25s", letterSpacing: "0.04em", minWidth: 130,
                      }}
                    >
                      {saved ? "✓ Saved!" : "Save Hook"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
          {/* 
        {!isLoading && (
          <div className="mt-1 pt-1" style={{ display: "flex", justifyContent: "space-between", gap: 8, borderTop: "dashed 1px var(--m2-line-divider-color)" }}>
            <div>
              {hookId && (<button className="btn btn-danger" onClick={fnDel} >ลบ</button>)}
            </div>
            <div className="d-flex justify-content-between" style={{ gap: "6px" }}>
              <button className="btn btn-warning" onClick={() => onClose(-1)} >ปิด</button>
              <button className="btn btn-primary" onClick={() => onSave()} >บันทึก</button>
            </div>
          </div>
        )} */}
        </div>
      </div >
    </ColorContext.Provider>,
    document.body
  );
}